import * as auth from "@script/auth";
import { images } from "@img/imagesMap";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useAuthActions } from "@hooks/useAuth";
import { useLoginModalActions, useLoginModalOpen } from "@hooks/useLoginModal";
import CustomModal from "@/components/modal/CustomModal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";
import useThrottle from "@hooks/useThrottle.ts";
import type { UserData } from "@/types/user-type";
import httpClient from "@/config/axios";
import { AxiosError } from "axios";
import formatPhoneNumber from "@/pages/myPage/util/formatPhone";
import { cn } from "@/utils/cn";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { X } from "lucide-react";

const loginSchema = z.object({
  username: z.string().min(1, "아이디를 입력해주세요."),
  password: z.string().min(1, "비밀번호를 입력해주세요."),
});

const findPasswordSchema = z
  .object({
    auth_method: z.enum(["phone", "email"]),
    memberID: z.string().min(1, "아이디를 입력해주세요."),
    EmployeeName: z.string().min(1, "이름을 입력해주세요."),
    receivenum: z.string().optional(),
    email: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.auth_method === "phone") {
      if (!data.receivenum?.trim()) {
        ctx.addIssue({
          path: ["receivenum"],
          message: "휴대폰 번호를 입력해주세요.",
          code: z.ZodIssueCode.custom,
        });
      } else {
        const digits = data.receivenum.replace(/\D/g, "");
        if (digits.length < 10 || !/^010\d{8}$/.test(digits)) {
          ctx.addIssue({
            path: ["receivenum"],
            message: "올바른 휴대폰 번호 형식이 아닙니다. (010-XXXX-XXXX)",
            code: z.ZodIssueCode.custom,
          });
        }
      }
    } else {
      if (!data.email?.trim()) {
        ctx.addIssue({
          path: ["email"],
          message: "이메일을 입력해주세요.",
          code: z.ZodIssueCode.custom,
        });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        ctx.addIssue({
          path: ["email"],
          message: "올바른 이메일 형식이 아닙니다.",
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

type LoginFormData = z.infer<typeof loginSchema>;
type FindPasswordFormData = z.infer<typeof findPasswordSchema>;

export default function LoginModal() {
  const [isFindPasswordOpen, setIsFindPasswordOpen] = useState(false);
  const throttle = useThrottle();

  const { setLoginUser } = useAuthActions();
  const { setClose } = useLoginModalActions();
  const isLoginModalOpen = useLoginModalOpen();

  const { register, handleSubmit, setValue, setFocus } = useForm<LoginFormData>(
    {
      resolver: zodResolver(loginSchema),
      mode: "onSubmit",
    }
  );

  const {
    mutate: mutateLogin,
    isPending: isLoginPending,
    isError: isLoginError,
    error: loginError,
    reset: resetLoginMutation,
  } = useMutation({
    mutationFn: async (data: LoginFormData) => {
      const body = new URLSearchParams({
        username: data.username.trim(),
        password: data.password.trim(),
        grant_type: "password",
      });

      try {
        // 501 에러 테스트용 (테스트 후 제거)
        // interceptor를 거친 형태로 에러 생성 - httpClient.post 호출 전에 주석 해제하여 테스트
        // const testError = new AxiosError(
        //   "정보가 일치하지 않거나 서비스 이용자가 아닙니다. 관리자에게 문의해주세요."
        // );
        // testError.response = {
        //   status: 501,
        //   statusText: "Not Implemented",
        //   data: {
        //     detail: "정보가 일치하지 않거나 서비스 이용자가 아닙니다. 관리자에게 문의해주세요.",
        //   },
        //   headers: {},
        //   config: {} as any,
        // };
        // testError.isAxiosError = true;
        // testError.message = "정보가 일치하지 않거나 서비스 이용자가 아닙니다. 관리자에게 문의해주세요.";
        // throw testError;

        await httpClient.post<{
          access_token: string;
          refresh_token: string;
        }>("/auth/login", body);

        return await auth.loadLoginUser();
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 501) {
          throw new Error(
            "정보가 일치하지 않거나 서비스 이용자가 아닙니다. 관리자에게 문의해주세요."
          );
        }

        if (error instanceof Error && error.message === "fail") {
          throw error;
        }

        // 기타 에러
        throw new Error("로그인에 실패했습니다. 입력 정보를 확인해주세요.");
      }
    },
    onSuccess: (getUserData) => {
      // 성공시 유저데이터 세팅 처리
      setLoginUser(getUserData as UserData);
      setClose(false);
      toast.success("로그인되었습니다.");
    },
  });

  function onSubmit(data: LoginFormData) {
    throttle(() => mutateLogin(data));
  }

  function clearInput(fieldName: keyof LoginFormData) {
    setValue(fieldName, "");
    setFocus(fieldName);
  }

  const isPending = isLoginPending;
  const loginErrorMessage = (() => {
    if (!isLoginError) return null;
    if (loginError instanceof Error && loginError.message === "fail") {
      return "아이디 또는 비밀번호가 올바르지 않습니다.";
    }
    return "로그인할 수 없습니다.\n네트워크 상태를 확인하거나 나중에 다시 시도해주세요.";
  })();

  function resetLoginError() {
    if (isLoginError) {
      resetLoginMutation();
    }
  }

  // 비밀번호 찾기 폼 (POST /auth/find_pw)
  const findPasswordForm = useForm<FindPasswordFormData>({
    resolver: zodResolver(findPasswordSchema),
    defaultValues: {
      auth_method: "phone",
      memberID: "",
      EmployeeName: "",
      receivenum: "",
      email: "",
    },
  });

  const authMethod = findPasswordForm.watch("auth_method");

  const findPasswordMutation = useMutation({
    mutationFn: async (data: FindPasswordFormData) => {
      const params = new URLSearchParams();
      params.set("auth_method", data.auth_method);
      params.set("memberID", data.memberID.trim());
      params.set("EmployeeName", data.EmployeeName.trim());
      if (data.auth_method === "phone" && data.receivenum) {
        params.set("receivenum", data.receivenum.replace(/-/g, "").trim());
      }
      if (data.auth_method === "email" && data.email) {
        params.set("email", data.email.trim());
      }
      const body = params.toString();
      return await httpClient.post<{
        result?: string;
        message?: string;
      }>("/auth/find_pw", body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
    },
    onSuccess: (payload) => {
      if (payload?.result === "fail") {
        toast.error(
          payload?.message ?? "요청에 실패했습니다. 입력 정보를 확인해주세요."
        );
        return;
      }
      const msg =
        payload?.message ||
        "임시비밀번호 발급이 완료되었습니다. 로그인 후 패스워드를 변경해 주세요.";
      toast.success(msg);
      setIsFindPasswordOpen(false);
      findPasswordForm.reset({
        auth_method: "phone",
        memberID: "",
        EmployeeName: "",
        receivenum: "",
        email: "",
      });
    },
  });

  function onFindPasswordSubmit(data: FindPasswordFormData) {
    findPasswordMutation.mutate(data);
  }

  return (
    <>
      <CustomModal.Panel
        onClose={() => setClose(false)}
        className="bg-transparent p-0 shadow-none max-h-[90vh] overflow-y-auto"
      >
        <div className="loginWindow max-w-[600px]">
          <div className="login_header">
            <img src={images.logo_aide} alt="" className="login_headerLogo" />
            <div className="login_headerText">
              <span className="ff-Tenada fs-36px">AI</span> 근무표
            </div>
            <button
              className="modal_button_close"
              type="button"
              onClick={() => {
                setClose(false);
              }}
            ></button>
          </div>
          <div className="login_subTitle">
            AI로 완성하는 근무표, 더 쉽고 더 스마트하게
          </div>

          <form
            onSubmit={handleSubmit(onSubmit, (errors) => {
              if (errors.username) alert(errors.username.message);
              else if (errors.password) alert(errors.password.message);
            })}
          >
            <div className="relative">
              <div className="login_inputWrap_container">
                <div
                  className={`login_inputWrap ${isPending ? "login_inputWrap--disabled" : ""}`}
                >
                  <input
                    type="text"
                    className="login_input"
                    disabled={isPending}
                    placeholder="아이디"
                    {...register("username", {
                      onChange: resetLoginError,
                    })}
                  />
                  <button
                    type="button"
                    className="login_inputClearButton"
                    onClick={() => clearInput("username")}
                    tabIndex={-1}
                  ></button>
                </div>
                <div
                  className={`login_inputWrap ${isPending ? "login_inputWrap--disabled" : ""}`}
                >
                  <input
                    type="password"
                    className="login_input"
                    disabled={isPending}
                    autoComplete="off"
                    placeholder="비밀번호"
                    {...register("password", {
                      onChange: resetLoginError,
                    })}
                  />
                  <button
                    type="button"
                    className="login_inputClearButton"
                    onClick={() => clearInput("password")}
                    tabIndex={-1}
                  ></button>
                </div>
              </div>
              {loginErrorMessage != null && (
                <div className="login_failModal">{loginErrorMessage}</div>
              )}
            </div>
            <button
              type="submit"
              className="block w-full rounded-[10px] bg-[linear-gradient(to_right,#07ace3,#0d5df7_42%,#fe0e82)] px-4 py-[1em] text-center font-['NanumSquare'] text-[20px] font-bold text-white disabled:bg-[linear-gradient(to_right,#d5e0e3,#e4ebf8_42%,#fbe1ee)]"
              disabled={isPending}
            >
              {isPending ? "로그인 중..." : "로그인"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => setIsFindPasswordOpen(true)}
            className="w-full text-center pt-5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer hover:underline"
          >
            비밀번호 찾기
          </button>
        </div>
      </CustomModal.Panel>

      {/* 비밀번호 찾기 모달 */}
      {isFindPasswordOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="absolute  inset-0 z-[100] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setIsFindPasswordOpen(false)}
              aria-hidden
            />
            <div
              className="relative bg-white rounded-[10px] shadow-lg px-12 py-6 w-full max-w-[450px] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 닫기 버튼 - 우측 상단 */}
              <button
                type="button"
                onClick={() => {
                  setIsFindPasswordOpen(false);
                  findPasswordForm.reset({
                    auth_method: "phone",
                    memberID: "",
                    EmployeeName: "",
                    receivenum: "",
                    email: "",
                  });
                }}
                className="absolute top-4 right-4 text-[#999] hover:text-[#333] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-lg  text-center text-[#333] mb-6 font-pretendard">
                비밀번호 찾기
              </h3>
              <form
                onSubmit={findPasswordForm.handleSubmit(onFindPasswordSubmit)}
                className="space-y-4"
              >
                {/* 인증 수단: 휴대폰 / 이메일 - 토글식 */}
                <div>
                  <label className="block text-sm font-medium text-[#555] mb-2 font-pretendard">
                    인증 수단
                  </label>
                  <ToggleGroup
                    type="single"
                    value={authMethod}
                    onValueChange={(value: string) => {
                      if (value === "phone" || value === "email") {
                        findPasswordForm.setValue("auth_method", value, {
                          shouldValidate: true,
                        });
                      }
                    }}
                    className="w-full"
                  >
                    <ToggleGroupItem value="phone" className="flex-1">
                      휴대폰
                    </ToggleGroupItem>
                    <ToggleGroupItem value="email" className="flex-1">
                      이메일
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#555] mb-1 font-pretendard">
                    아이디
                  </label>
                  <input
                    type="text"
                    placeholder="아이디를 입력해주세요"
                    className={cn(
                      "w-full border rounded-lg px-3 py-2.5 text-sm outline-none font-pretendard",
                      findPasswordForm.formState.errors.memberID
                        ? "border-red-400"
                        : "border-[#D6D6DE]"
                    )}
                    {...findPasswordForm.register("memberID")}
                  />
                  {findPasswordForm.formState.errors.memberID && (
                    <p className="mt-1 text-xs text-red-500 font-pretendard">
                      {findPasswordForm.formState.errors.memberID.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#555] mb-1 font-pretendard">
                    이름
                  </label>
                  <input
                    type="text"
                    placeholder="이름을 입력해주세요"
                    className={cn(
                      "w-full border rounded-lg px-3 py-2.5 text-sm outline-none font-pretendard",
                      findPasswordForm.formState.errors.EmployeeName
                        ? "border-red-400"
                        : "border-[#D6D6DE]"
                    )}
                    {...findPasswordForm.register("EmployeeName")}
                  />
                  {findPasswordForm.formState.errors.EmployeeName && (
                    <p className="mt-1 text-xs text-red-500 font-pretendard">
                      {findPasswordForm.formState.errors.EmployeeName.message}
                    </p>
                  )}
                </div>
                {authMethod === "phone" ? (
                  <div>
                    <label className="block text-sm font-medium text-[#555] mb-1 font-pretendard">
                      휴대폰 번호
                    </label>
                    <input
                      type="text"
                      placeholder="010-0000-0000"
                      maxLength={13}
                      className={cn(
                        "w-full border rounded-lg px-3 py-2.5 text-sm outline-none font-pretendard",
                        findPasswordForm.formState.errors.receivenum
                          ? "border-red-400"
                          : "border-[#D6D6DE]"
                      )}
                      {...findPasswordForm.register("receivenum")}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        findPasswordForm.setValue("receivenum", formatted, {
                          shouldValidate: true,
                        });
                      }}
                    />
                    {findPasswordForm.formState.errors.receivenum && (
                      <p className="mt-1 text-xs text-red-500 font-pretendard">
                        {findPasswordForm.formState.errors.receivenum.message}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-[#555] mb-1 font-pretendard">
                      이메일
                    </label>
                    <input
                      type="email"
                      placeholder="example@email.com"
                      className={cn(
                        "w-full border rounded-lg px-3 py-2.5 text-sm outline-none font-pretendard",
                        findPasswordForm.formState.errors.email
                          ? "border-red-400"
                          : "border-[#D6D6DE]"
                      )}
                      {...findPasswordForm.register("email")}
                    />
                    {findPasswordForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-red-500 font-pretendard">
                        {findPasswordForm.formState.errors.email?.message}
                      </p>
                    )}
                  </div>
                )}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={findPasswordMutation.isPending}
                    className="w-full py-2.5 rounded-lg bg-sig text-white text-sm font-medium hover:bg-[#0052CC] disabled:opacity-60 font-pretendard"
                  >
                    {findPasswordMutation.isPending
                      ? "요청 중..."
                      : "임시 비밀번호 전송"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.querySelector(".c_main__container") || document.body
        )}
    </>
  );
}
